import { useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { fetchAdminPermissions } from '../../api'
import type { AdminRole } from '../../types'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'

interface RoleDetailDialogProps {
  role: AdminRole | null
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function RoleDetailDialog({ role, open, onOpenChange }: RoleDetailDialogProps) {
  const { t } = useTranslation()

  const permissionsQuery = useQuery({
    queryKey: ['admin', 'permissions'],
    queryFn: fetchAdminPermissions,
    enabled: open,
  })

  const allGroups = permissionsQuery.data?.data ?? {}
  const rolePermSet = new Set(role?.permissions ?? [])

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <DialogTitle>{role?.name}</DialogTitle>
            {role?.is_system && (
              <Badge variant="secondary" className="text-xs">
                {t('admin.roles.systemBadge')}
              </Badge>
            )}
          </div>
          <DialogDescription>{t('admin.roles.dialogs.detail.title')}</DialogDescription>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto pr-4">
          {permissionsQuery.isPending ? (
            <div className="space-y-3">
              {Array.from({ length: 6 }).map((_, i) => (
                <Skeleton key={i} className="h-8 w-full" />
              ))}
            </div>
          ) : (
            <div className="space-y-5">
              {Object.entries(allGroups).map(([resource, perms]) => {
                const activePerms = perms.filter((p) => rolePermSet.has(p.permission_id))
                if (activePerms.length === 0) return null
                return (
                  <div key={resource}>
                    <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                      {resource}
                    </p>
                    <div className="flex flex-wrap gap-1.5">
                      {activePerms.map((perm) => (
                        <Badge
                          key={perm.permission_id}
                          variant="outline"
                          className="rounded-full px-2.5 py-0.5 text-xs font-normal"
                          title={perm.description}
                        >
                          {perm.action}
                        </Badge>
                      ))}
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t('common.close')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
